import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { ScaleLoader } from 'react-spinners';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faCamera,
  faMapMarkerAlt,
  faClock,
  faCar,
  faUser,
  faWrench,
  faFileAlt,
  faExclamationCircle,
  faEnvelope,
  faPhone,
  faExternalLinkAlt,
  faReply,
  faCheckCircle,
  faHourglass,
  faPlus,
  faImages,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { useQuery, useMutation, gql } from '@apollo/client';
import { toast } from 'react-toastify';
import {
  GET_DAMAGE_CLAIM,
  RESPOND_TO_DAMAGE_CLAIM,
} from '../../../services/damageClaimService/queries';
import { useCanRespondToClaimQuery } from '../../../services/damageClaimService/__generated__/queries.generated';
import { getSiteProductKind } from '../../../constants/siteConfig';
import { bookingDetailPath, claimListPath } from '../../../utils/productRoutes';
import { toPublicS3ObjectUrl, resolveTripPhotoUrl } from '../../../utils/s3PublicUrl';
import { authStore } from '../../../../store/authStore';
import {
  DamageClaimStatus,
  DamageType,
  PhysicalDamageSubcategory,
  YesNoUnknown,
  DriverAtIncident,
  RepairEstimateRange,
  DamagedPart,
  MechanicalIssueType,
  ProductType,
} from '../../../../__generated__/graphql';

const PageContainer = styled.div`
  ${tw`flex flex-col w-full max-w-full min-w-0 h-full max-w-screen-xl mx-auto pb-10 md:pb-16 box-border`}
`;

const Header = styled.div`
  ${tw`flex items-center gap-4 px-4 lg:px-12 mt-6 md:mt-8 mb-6`}
`;

const BackButton = styled.button`
  ${tw`p-2 text-text-secondary hover:text-text-secondary rounded-full hover:bg-bg-secondary`}
`;

const HeaderContent = styled.div`
  ${tw`flex-1`}
`;

const Title = styled.h1`
  ${tw`text-2xl md:text-3xl font-bold text-text-primary`}
`;

const Subtitle = styled.p`
  ${tw`text-sm text-text-secondary`}
`;

const LoadingContainer = styled.div`
  ${tw`w-full flex justify-center items-center mt-20`}
`;

const ErrorState = styled.div`
  ${tw`w-full text-center text-text-secondary mt-20 px-4`}
`;

const ContentContainer = styled.div`
  ${tw`w-full max-w-full min-w-0 box-border px-4 lg:px-12`}
`;

const StatusBadge = styled.span<{ status: DamageClaimStatus }>`
  ${tw`px-3 py-1 rounded-full text-sm font-medium max-w-full min-w-0 box-border`}
  ${props => {
    switch (props.status) {
      case DamageClaimStatus.Pending:
        return tw`bg-yellow-100 text-yellow-800`;
      case DamageClaimStatus.UnderReview:
        return tw`bg-blue-100 text-blue-800`;
      case DamageClaimStatus.Approved:
        return tw`bg-green-100 text-green-800`;
      case DamageClaimStatus.PartiallyApproved:
        return tw`bg-green-100 text-green-700`;
      case DamageClaimStatus.Rejected:
        return tw`bg-red-100 text-red-800`;
      case DamageClaimStatus.Resolved:
        return tw`bg-bg-secondary text-text-primary`;
      case DamageClaimStatus.Withdrawn:
        return tw`bg-bg-secondary text-text-secondary`;
      default:
        return tw`bg-bg-secondary text-text-primary`;
    }
  }}
`;

const Section = styled.div`
  ${tw`bg-white rounded-card shadow-card border border-border-default p-6 mb-6`}
`;

const SectionTitle = styled.h2`
  ${tw`text-lg font-semibold text-text-primary mb-4 flex items-center gap-2`}
`;

const SectionIcon = styled.span`
  ${tw`text-black`}
`;

const Grid = styled.div`
  ${tw`grid grid-cols-1 md:grid-cols-2 gap-4`}
`;

const InfoItem = styled.div`
  ${tw`flex flex-col`}
`;

const InfoLabel = styled.span`
  ${tw`text-sm text-text-secondary mb-1`}
`;

const InfoValue = styled.span`
  ${tw`text-text-primary`}
`;

const PhotosGrid = styled.div`
  ${tw`grid grid-cols-2 md:grid-cols-4 gap-4`}
`;

const PhotoThumbnail = styled.img`
  ${tw`w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity`}
`;

const NoPhotos = styled.p`
  ${tw`text-text-tertiary italic`}
`;

const DamagedPartsList = styled.div`
  ${tw`flex flex-wrap gap-2`}
`;

const DamagedPartTag = styled.span`
  ${tw`px-3 py-1 bg-bg-secondary text-text-secondary rounded-full text-sm`}
`;

const CarInfoCard = styled.div`
  ${tw`flex items-center gap-4`}
`;

const CarThumbnail = styled.img`
  ${tw`w-20 h-16 object-cover rounded-lg`}
`;

const CarDetails = styled.div`
  ${tw`flex-1`}
`;

const CarName = styled.p`
  ${tw`font-medium text-text-primary`}
`;

const TripDates = styled.p`
  ${tw`text-sm text-text-secondary`}
`;

const RepairShopCard = styled.div`
  ${tw`bg-bg-secondary rounded-lg p-4`}
`;

const BookingLink = styled(Link)`
  ${tw`text-black hover:text-black font-medium flex items-center gap-2`}
`;

const ContactGrid = styled.div`
  ${tw`grid grid-cols-1 md:grid-cols-2 gap-6 mt-4 pt-4 border-t border-border-default`}
`;

const ContactCard = styled.div`
  ${tw`bg-bg-secondary rounded-lg p-4`}
`;

const ContactTitle = styled.h4`
  ${tw`font-medium text-text-primary mb-3 flex items-center gap-2`}
`;

const ContactInfo = styled.div`
  ${tw`space-y-2`}
`;

const ContactItem = styled.div`
  ${tw`flex items-center gap-2 text-sm text-text-secondary`}
`;

// Response Section Styles
const ResponseSection = styled(Section)`
  ${tw`border-2 border-border-default bg-bg-secondary`}
`;

const ResponseDeadlineBar = styled.div<{ isExpired: boolean }>`
  ${tw`flex items-center gap-2 px-4 py-3 rounded-lg mb-4`}
  ${props => (props.isExpired ? tw`bg-red-100 text-red-700` : tw`bg-yellow-100 text-yellow-700`)}
`;

const ExistingResponse = styled.div`
  ${tw`bg-white rounded-lg p-4 border border-border-default`}
`;

const ResponseHeader = styled.div`
  ${tw`flex items-center gap-2 mb-3`}
`;

const ResponderName = styled.span`
  ${tw`font-medium text-text-primary`}
`;

const ResponseDate = styled.span`
  ${tw`text-sm text-text-secondary`}
`;

const ResponseContent = styled.p`
  ${tw`text-text-secondary whitespace-pre-wrap`}
`;

const ResponseForm = styled.form`
  ${tw`mt-4`}
`;

const ResponseTextarea = styled.textarea`
  ${tw`w-full px-4 py-3 border border-border-default rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent`}
  min-height: 120px;
`;

const SubmitButton = styled.button<{ disabled?: boolean }>`
  ${tw`mt-3 px-6 py-3 bg-black text-white font-medium rounded-lg hover:bg-black transition-colors flex items-center gap-2`}
  ${props => props.disabled && tw`opacity-50 cursor-not-allowed`}
`;

const ResponsePhotoActions = styled.div`
  ${tw`flex flex-wrap gap-2 mt-2 mb-3`}
`;

const SecondaryButton = styled.button`
  ${tw`px-4 py-2 text-sm font-medium border border-border-default rounded-lg hover:bg-bg-secondary flex items-center gap-2`}
`;

/** Native `type="file"` must not show the browser "Choose file" control — use twin `hidden` (not only `className="hidden"`) so it stays off-screen in all builds. */
const HiddenMultiFileInput = styled.input`
  ${tw`hidden`}
`;

const TripPickerBackdrop = styled.div`
  ${tw`fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4`}
`;

const TripPickerPanel = styled.div`
  ${tw`bg-white rounded-card max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-overlay`}
`;

const TripPickerHeader = styled.div`
  ${tw`flex items-center justify-between px-4 py-3 border-b border-border-default`}
`;

const TripPickerScroll = styled.div`
  ${tw`overflow-y-auto p-4`}
`;

const TripThumbGrid = styled.div`
  ${tw`grid grid-cols-3 sm:grid-cols-4 gap-2`}
`;

const TripThumb = styled.button<{ $selected?: boolean }>`
  ${tw`relative aspect-square rounded-lg overflow-hidden border-2`}
  ${p => (p.$selected ? tw`border-black ring-2 ring-pink-200` : tw`border-transparent`)}
`;

const TripThumbImg = styled.img`
  ${tw`w-full h-full object-cover`}
`;

const ResponsePhotoPreviewGrid = styled.div`
  ${tw`grid grid-cols-4 gap-2 mt-2 mb-4`}
`;

const ResponsePreviewCard = styled.div`
  ${tw`relative aspect-square rounded-lg overflow-hidden`}
`;

const ResponseThumbImg = styled.img`
  ${tw`w-full h-full object-cover`}
`;

const RemovePreviewBtn = styled.button`
  ${tw`absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs`}
`;

const MAX_RESPONSE_PHOTOS = 8;

const GET_PRESIGNED_FOR_CLAIM_PHOTO = gql`
  mutation GetPresignedUrlForClaimResponsePhoto(
    $fileType: String!
    $claimId: String!
    $photoType: String!
  ) {
    getPresignedUrlForDamageClaim(fileType: $fileType, claimId: $claimId, photoType: $photoType) {
      url
      key
    }
  }
`;

const ResponseStatusBadge = styled.span<{ reviewed: boolean }>`
  ${tw`px-3 py-1 rounded-full text-sm font-medium`}
  ${props => (props.reviewed ? tw`bg-green-100 text-green-800` : tw`bg-yellow-100 text-yellow-800`)}
`;

const NoResponseNeeded = styled.div`
  ${tw`flex items-center gap-2 text-text-secondary italic`}
`;

// Helper functions
const getStatusLabel = (status: DamageClaimStatus): string => {
  switch (status) {
    case DamageClaimStatus.Pending:
      return 'Pending';
    case DamageClaimStatus.UnderReview:
      return 'Under Review';
    case DamageClaimStatus.Approved:
      return 'Approved';
    case DamageClaimStatus.PartiallyApproved:
      return 'Partially Approved';
    case DamageClaimStatus.Rejected:
      return 'Rejected';
    case DamageClaimStatus.Resolved:
      return 'Resolved';
    case DamageClaimStatus.Withdrawn:
      return 'Withdrawn';
    default:
      return status;
  }
};

const getDamageTypeLabel = (type: DamageType): string => {
  switch (type) {
    case DamageType.Physical:
      return 'Physical Damage';
    case DamageType.Mechanical:
      return 'Mechanical Issue';
    default:
      return type;
  }
};

const getSubcategoryLabel = (subcategory: PhysicalDamageSubcategory): string => {
  switch (subcategory) {
    case PhysicalDamageSubcategory.Collision:
      return 'Collision';
    case PhysicalDamageSubcategory.TheftVandalism:
      return 'Theft or Vandalism';
    case PhysicalDamageSubcategory.WeatherNaturalDisaster:
      return 'Weather or Natural Disaster';
    case PhysicalDamageSubcategory.SomethingElse:
      return 'Something Else';
    default:
      return subcategory;
  }
};

const getYesNoLabel = (value: YesNoUnknown): string => {
  switch (value) {
    case YesNoUnknown.Yes:
      return 'Yes';
    case YesNoUnknown.No:
      return 'No';
    case YesNoUnknown.Unknown:
      return "I don't know";
    default:
      return value;
  }
};

const getDriverLabel = (driver: DriverAtIncident): string => {
  switch (driver) {
    case DriverAtIncident.Guest:
      return 'Guest';
    case DriverAtIncident.SomeoneElse:
      return 'Someone Else';
    case DriverAtIncident.NotBeingDriven:
      return 'Vehicle was not being driven';
    case DriverAtIncident.Unknown:
      return "I don't know";
    default:
      return driver;
  }
};

const getEstimateLabel = (estimate: RepairEstimateRange): string => {
  switch (estimate) {
    case RepairEstimateRange.Below_500:
      return 'Below $500';
    case RepairEstimateRange.Range_500_1000:
      return '$500 - $1,000';
    case RepairEstimateRange.Range_1000_2000:
      return '$1,000 - $2,000';
    case RepairEstimateRange.Over_2000:
      return 'Over $2,000';
    case RepairEstimateRange.Unknown:
      return "I don't know";
    default:
      return estimate;
  }
};

const getDamagedPartLabel = (part: DamagedPart): string => {
  const labels: Record<DamagedPart, string> = {
    [DamagedPart.DriverSideBody]: 'Driver Side Body',
    [DamagedPart.FrontBumperOrGrille]: 'Front Bumper/Grille',
    [DamagedPart.FrontHood]: 'Front Hood',
    [DamagedPart.PassengerSideBody]: 'Passenger Side Body',
    [DamagedPart.RearBumper]: 'Rear Bumper',
    [DamagedPart.Roof]: 'Roof',
    [DamagedPart.Trunk]: 'Trunk',
    [DamagedPart.DriverSideFrontTireOrRim]: 'Driver Front Tire/Rim',
    [DamagedPart.DriverSideRearTireOrRim]: 'Driver Rear Tire/Rim',
    [DamagedPart.PassengerSideFrontTireOrRim]: 'Passenger Front Tire/Rim',
    [DamagedPart.PassengerSideRearTireOrRim]: 'Passenger Rear Tire/Rim',
    [DamagedPart.DriverSideWindowsOrMirrors]: 'Driver Side Windows/Mirrors',
    [DamagedPart.PassengerSideWindowsOrMirrors]: 'Passenger Side Windows/Mirrors',
    [DamagedPart.FrontWindshield]: 'Front Windshield',
    [DamagedPart.RearWindshield]: 'Rear Windshield',
    [DamagedPart.InteriorDamage]: 'Interior Damage',
    [DamagedPart.MechanicalDamage]: 'Mechanical Damage',
    [DamagedPart.HeadlightsOrTailLights]: 'Headlights/Tail Lights',
    [DamagedPart.Other]: 'Other',
  };
  return labels[part] || part;
};

const getMechanicalIssueLabel = (issue: MechanicalIssueType): string => {
  const labels: Record<MechanicalIssueType, string> = {
    [MechanicalIssueType.CheckEngineLight]: 'Check Engine Light',
    [MechanicalIssueType.BrakesIssue]: 'Brakes Issue',
    [MechanicalIssueType.ClutchIssue]: 'Clutch Issue',
    [MechanicalIssueType.BatteryIssue]: 'Battery Issue',
    [MechanicalIssueType.BrokeDownNotDriveable]: 'Broke Down / Not Driveable',
    [MechanicalIssueType.Other]: 'Other',
  };
  return labels[issue] || issue;
};

const formatDate = (dateValue: string | number | Date): string => {
  let date: Date;
  if (typeof dateValue === 'string') {
    const parsed = parseInt(dateValue);
    if (!isNaN(parsed) && String(parsed) === dateValue) {
      date = new Date(parsed);
    } else {
      date = new Date(dateValue);
    }
  } else {
    date = new Date(dateValue);
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatDateTime = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const formatTripDates = (startAt: string | number, endAt: string | number): string => {
  const start = new Date(typeof startAt === 'string' ? parseInt(startAt) : startAt);
  const end = new Date(typeof endAt === 'string' ? parseInt(endAt) : endAt);
  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
};

const getTimeRemaining = (
  deadline: string | number | Date,
): { text: string; isExpired: boolean } => {
  const deadlineDate = new Date(deadline);
  const now = new Date();
  const diff = deadlineDate.getTime() - now.getTime();

  if (diff <= 0) {
    return { text: 'Response deadline has passed', isExpired: true };
  }

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return { text: `${days} day${days > 1 ? 's' : ''} remaining to respond`, isExpired: false };
  }

  if (hours > 0) {
    return { text: `${hours}h ${minutes}m remaining to respond`, isExpired: false };
  }

  return { text: `${minutes} minutes remaining to respond`, isExpired: false };
};

const DamageClaimDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { claimId } = useParams<{ claimId: string }>();
  const { isAuthenticated } = useSnapshot(authStore);
  const [responseText, setResponseText] = useState('');
  const [timeRemaining, setTimeRemaining] = useState<{ text: string; isExpired: boolean } | null>(
    null,
  );
  const [responseTripUrls, setResponseTripUrls] = useState<string[]>([]);
  const [responseFiles, setResponseFiles] = useState<File[]>([]);
  const [responseFilePreviewUrls, setResponseFilePreviewUrls] = useState<string[]>([]);
  const [tripPickerOpen, setTripPickerOpen] = useState(false);
  const responseFileInputRef = useRef<HTMLInputElement>(null);

  const { data, loading, error, refetch } = useQuery(GET_DAMAGE_CLAIM, {
    variables: { id: claimId || '' },
    skip: !isAuthenticated || !claimId,
  });

  const claim = data?.damageClaim;
  const isGuestRoute = location.pathname.startsWith('/guest/');
  const viewer = isGuestRoute ? 'guest' : 'host';
  // Spec 016 W25: a stay's claim is a Resolution Center request, shown with the place's
  // name and the stay pages; the car host pages redirect home on the stays site.
  const isStay = claim?.booking?.productType === ProductType.House;
  const kind = isStay ? 'stay' : getSiteProductKind();
  const claimsBackPath = claimListPath(viewer, kind);
  const bookingPath = (bookingId: string) => bookingDetailPath(viewer, bookingId, kind);

  const tripPhotoAllowlist = useMemo(() => {
    const b = claim?.booking as
      | {
          tripStartImages?: Array<{ url?: string | null } | null> | null;
          tripEndImages?: Array<{ url?: string | null } | null> | null;
        }
      | undefined;
    if (!b) return new Set<string>();
    const set = new Set<string>();
    const addRows = (rows?: Array<{ url?: string | null } | null> | null) => {
      rows?.forEach(row => {
        if (row?.url) set.add(resolveTripPhotoUrl(row.url));
      });
    };
    addRows(b.tripStartImages);
    addRows(b.tripEndImages);
    return set;
  }, [claim?.booking]);

  const tripPickerItems = useMemo(() => {
    const b = claim?.booking as
      | {
          tripStartImages?: Array<{ url?: string | null } | null> | null;
          tripEndImages?: Array<{ url?: string | null } | null> | null;
        }
      | undefined;
    if (!b) return [] as { key: string; resolved: string; label: string }[];
    const items: { key: string; resolved: string; label: string }[] = [];
    let i = 0;
    b.tripStartImages?.forEach(row => {
      if (row?.url) {
        const resolved = resolveTripPhotoUrl(row.url);
        items.push({ key: `s-${i++}`, resolved, label: 'Start' });
      }
    });
    b.tripEndImages?.forEach(row => {
      if (row?.url) {
        const resolved = resolveTripPhotoUrl(row.url);
        items.push({ key: `e-${i++}`, resolved, label: 'End' });
      }
    });
    return items;
  }, [claim?.booking]);

  const { data: canRespondData } = useCanRespondToClaimQuery({
    variables: { claimId: claimId || '' },
    skip: !isAuthenticated || !claimId,
  });

  const [getPresignedForClaimPhoto] = useMutation(GET_PRESIGNED_FOR_CLAIM_PHOTO);

  const [respondToClaim, { loading: respondLoading }] = useMutation(RESPOND_TO_DAMAGE_CLAIM, {
    onCompleted: () => {
      setResponseText('');
      setResponseTripUrls([]);
      setResponseFiles([]);
      setResponseFilePreviewUrls(prev => {
        prev.forEach(u => URL.revokeObjectURL(u));
        return [];
      });
      refetch();
    },
  });

  const responsePreviewUrlsRef = useRef<string[]>([]);
  responsePreviewUrlsRef.current = responseFilePreviewUrls;
  useEffect(() => {
    return () => {
      responsePreviewUrlsRef.current.forEach(u => URL.revokeObjectURL(u));
    };
  }, []);

  // Update time remaining every minute
  useEffect(() => {
    if (claim?.responseDeadline) {
      const updateTime = () => {
        setTimeRemaining(getTimeRemaining(claim.responseDeadline));
      };
      updateTime();
      const interval = setInterval(updateTime, 60000);
      return () => clearInterval(interval);
    }
  }, [claim?.responseDeadline]);

  const uploadResponseFileToS3 = useCallback(
    async (file: File, realClaimId: string): Promise<string> => {
      const { data: presignData } = await getPresignedForClaimPhoto({
        variables: {
          fileType: file.type || 'image/jpeg',
          claimId: realClaimId,
          photoType: 'response',
        },
      });
      const presignedUrl = presignData?.getPresignedUrlForDamageClaim?.url;
      const key = presignData?.getPresignedUrlForDamageClaim?.key;
      if (!presignedUrl || !key) throw new Error('Failed to get upload URL');
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'image/jpeg' },
      });
      if (!uploadResponse.ok) {
        throw new Error(`Upload failed (${uploadResponse.status})`);
      }
      return toPublicS3ObjectUrl(key);
    },
    [getPresignedForClaimPhoto],
  );

  const totalResponsePhotoCount = responseTripUrls.length + responseFiles.length;

  const toggleTripPhoto = useCallback(
    (resolvedUrl: string) => {
      setResponseTripUrls(prev => {
        if (prev.includes(resolvedUrl)) return prev.filter(u => u !== resolvedUrl);
        if (prev.length + responseFiles.length >= MAX_RESPONSE_PHOTOS) {
          toast.info(`Maximum ${MAX_RESPONSE_PHOTOS} photos`);
          return prev;
        }
        if (!tripPhotoAllowlist.has(resolvedUrl)) {
          toast.error('Invalid photo selection');
          return prev;
        }
        return [...prev, resolvedUrl];
      });
    },
    [responseFiles.length, tripPhotoAllowlist],
  );

  const onResponseFilesSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files?.length) return;
      const next: File[] = [];
      const previews: string[] = [];
      let remaining = MAX_RESPONSE_PHOTOS - responseTripUrls.length - responseFiles.length;
      for (const f of Array.from(files)) {
        if (remaining <= 0) break;
        if (!f.type.startsWith('image/')) continue;
        next.push(f);
        previews.push(URL.createObjectURL(f));
        remaining -= 1;
      }
      if (next.length < files.length) {
        toast.info(`Maximum ${MAX_RESPONSE_PHOTOS} photos`);
      }
      setResponseFiles(prev => [...prev, ...next]);
      setResponseFilePreviewUrls(prev => [...prev, ...previews]);
      e.target.value = '';
    },
    [responseFiles.length, responseTripUrls.length],
  );

  const removeResponseFile = useCallback((index: number) => {
    setResponseFiles(prev => prev.filter((_, i) => i !== index));
    setResponseFilePreviewUrls(prev => {
      const url = prev[index];
      if (url) URL.revokeObjectURL(url);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const removeResponseTripUrl = useCallback((url: string) => {
    setResponseTripUrls(prev => prev.filter(u => u !== url));
  }, []);

  const handleSubmitResponse = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!responseText.trim() || !claimId || !claim?.id) return;

    try {
      const uploadedUrls: string[] = [];
      for (const file of responseFiles) {
        uploadedUrls.push(await uploadResponseFileToS3(file, claim.id));
      }
      const invalidTrip = responseTripUrls.filter(u => !tripPhotoAllowlist.has(u));
      if (invalidTrip.length) {
        toast.error('One or more trip photos are not valid for this booking');
        return;
      }
      const responsePhotos = [...responseTripUrls, ...uploadedUrls].filter(Boolean);
      await respondToClaim({
        variables: {
          input: {
            claimId,
            responseContent: responseText.trim(),
            responsePhotos: responsePhotos.length ? responsePhotos : undefined,
          },
        },
      });
      toast.success('Response submitted');
    } catch (err: any) {
      console.error('Failed to submit response:', err);
      toast.error(err?.message || 'Failed to submit response');
    }
  };

  if (!isAuthenticated) {
    navigate('/', { replace: true });
    return null;
  }

  if (loading) {
    return (
      <PageContainer>
        <Header>
          <BackButton onClick={() => navigate(claimsBackPath)}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </BackButton>
          <Title>Loading...</Title>
        </Header>
        <LoadingContainer>
          <ScaleLoader color="#EC4899" />
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (error || !claim) {
    return (
      <PageContainer>
        <Header>
          <BackButton onClick={() => navigate(claimsBackPath)}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </BackButton>
          <Title>Error</Title>
        </Header>
        <ErrorState>
          <FontAwesomeIcon icon={faExclamationCircle} className="text-6xl text-gray-300 mb-4" />
          <p className="text-lg font-medium text-gray-700 mb-2">Claim not found</p>
          <p>{error?.message || 'Unable to load claim details'}</p>
        </ErrorState>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <BackButton onClick={() => navigate(claimsBackPath)}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </BackButton>
        <HeaderContent>
          <Title>
            {isStay ? 'Request' : 'Claim'} #{claim.claimNum}
          </Title>
          <Subtitle>Submitted {formatDate(claim.createdAt)}</Subtitle>
        </HeaderContent>
        <StatusBadge status={claim.status}>{getStatusLabel(claim.status)}</StatusBadge>
      </Header>

      <ContentContainer>
        {/* Car & Trip Info */}
        {claim.booking && (
          <Section>
            <SectionTitle>
              <SectionIcon>
                <FontAwesomeIcon icon={faCar} />
              </SectionIcon>
              {isStay ? 'Place & Stay' : 'Vehicle & Trip'}
            </SectionTitle>
            <CarInfoCard>
              {claim.booking.car?.thumbnailUrl && (
                <CarThumbnail
                  src={toPublicS3ObjectUrl(claim.booking.car.thumbnailUrl)}
                  alt={
                    isStay
                      ? claim.booking.car.name || 'Place to stay'
                      : `${claim.booking.car.make} ${claim.booking.car.model}`
                  }
                />
              )}
              <CarDetails>
                <CarName>
                  {isStay
                    ? claim.booking.car?.name || 'Place to stay'
                    : `${claim.booking.car?.year ?? ''} ${claim.booking.car?.make ?? ''} ${
                        claim.booking.car?.model ?? ''
                      }`.trim()}
                </CarName>
                <TripDates>
                  {isStay ? 'Stay' : 'Trip'}:{' '}
                  {formatTripDates(claim.booking.startAt, claim.booking.endAt)}
                </TripDates>
              </CarDetails>
            </CarInfoCard>

            {/* Booking Link */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <InfoLabel>Booking ID</InfoLabel>
              <BookingLink to={bookingPath(claim.booking.id)}>
                {claim.booking.id}
                <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs" />
              </BookingLink>
            </div>

            {claim.reporter && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <InfoLabel>Reported by</InfoLabel>
                <InfoValue>
                  {claim.reporter.displayName ||
                    `${claim.reporter.firstName} ${claim.reporter.lastName}`}
                </InfoValue>
              </div>
            )}

            {/* Guest & Host Contact Info */}
            <ContactGrid>
              {/* Guest Info */}
              {claim.booking.guest && (
                <ContactCard>
                  <ContactTitle>
                    <FontAwesomeIcon icon={faUser} className="text-pink-500" />
                    Guest
                  </ContactTitle>
                  <ContactInfo>
                    <ContactItem>
                      <span className="font-medium text-gray-900">
                        {claim.booking.guest.displayName ||
                          `${claim.booking.guest.firstName} ${claim.booking.guest.lastName}`}
                      </span>
                    </ContactItem>
                    {claim.booking.guest.email && (
                      <ContactItem>
                        <FontAwesomeIcon icon={faEnvelope} className="text-gray-400 w-4" />
                        <a
                          href={`mailto:${claim.booking.guest.email}`}
                          className="text-pink-600 hover:text-pink-700"
                        >
                          {claim.booking.guest.email}
                        </a>
                      </ContactItem>
                    )}
                    {claim.booking.guest.phoneNumber && (
                      <ContactItem>
                        <FontAwesomeIcon icon={faPhone} className="text-gray-400 w-4" />
                        <a
                          href={`tel:${claim.booking.guest.phoneNumber}`}
                          className="text-pink-600 hover:text-pink-700"
                        >
                          {claim.booking.guest.phoneNumber}
                        </a>
                      </ContactItem>
                    )}
                  </ContactInfo>
                </ContactCard>
              )}

              {/* Host Info */}
              {claim.booking.host && (
                <ContactCard>
                  <ContactTitle>
                    <FontAwesomeIcon icon={faUser} className="text-pink-500" />
                    Host
                  </ContactTitle>
                  <ContactInfo>
                    <ContactItem>
                      <span className="font-medium text-gray-900">
                        {claim.booking.host.displayName ||
                          `${claim.booking.host.firstName} ${claim.booking.host.lastName}`}
                      </span>
                    </ContactItem>
                    {claim.booking.host.email && (
                      <ContactItem>
                        <FontAwesomeIcon icon={faEnvelope} className="text-gray-400 w-4" />
                        <a
                          href={`mailto:${claim.booking.host.email}`}
                          className="text-pink-600 hover:text-pink-700"
                        >
                          {claim.booking.host.email}
                        </a>
                      </ContactItem>
                    )}
                  </ContactInfo>
                </ContactCard>
              )}
            </ContactGrid>
          </Section>
        )}

        {/* Damage Type */}
        <Section>
          <SectionTitle>
            <SectionIcon>
              <FontAwesomeIcon icon={faExclamationCircle} />
            </SectionIcon>
            Damage Information
          </SectionTitle>
          <Grid>
            <InfoItem>
              <InfoLabel>Damage Type</InfoLabel>
              <InfoValue>{getDamageTypeLabel(claim.damageType)}</InfoValue>
            </InfoItem>
            {claim.physicalDamageSubcategory && (
              <InfoItem>
                <InfoLabel>Category</InfoLabel>
                <InfoValue>{getSubcategoryLabel(claim.physicalDamageSubcategory)}</InfoValue>
              </InfoItem>
            )}
            {claim.repairEstimateRange && (
              <InfoItem>
                <InfoLabel>Repair Estimate</InfoLabel>
                <InfoValue>{getEstimateLabel(claim.repairEstimateRange)}</InfoValue>
              </InfoItem>
            )}
            {claim.approvedAmount && (
              <InfoItem>
                <InfoLabel>Approved Amount</InfoLabel>
                <InfoValue className="text-green-600 font-semibold">
                  ${claim.approvedAmount.toFixed(2)}
                </InfoValue>
              </InfoItem>
            )}
          </Grid>
        </Section>

        {/* Damaged Parts or Mechanical Issues */}
        {claim.damageType === DamageType.Physical &&
          claim.damagedParts &&
          claim.damagedParts.length > 0 && (
            <Section>
              <SectionTitle>
                <SectionIcon>
                  <FontAwesomeIcon icon={faWrench} />
                </SectionIcon>
                Damaged Parts
              </SectionTitle>
              <DamagedPartsList>
                {claim.damagedParts.map((part: DamagedPart) => (
                  <DamagedPartTag key={part}>{getDamagedPartLabel(part)}</DamagedPartTag>
                ))}
              </DamagedPartsList>
              {claim.damagedPartsOtherDescription && (
                <p className="mt-3 text-gray-600">{claim.damagedPartsOtherDescription}</p>
              )}
            </Section>
          )}

        {claim.damageType === DamageType.Mechanical &&
          claim.mechanicalIssues &&
          claim.mechanicalIssues.length > 0 && (
            <Section>
              <SectionTitle>
                <SectionIcon>
                  <FontAwesomeIcon icon={faWrench} />
                </SectionIcon>
                Mechanical Issues
              </SectionTitle>
              <DamagedPartsList>
                {claim.mechanicalIssues.map((issue: MechanicalIssueType) => (
                  <DamagedPartTag key={issue}>{getMechanicalIssueLabel(issue)}</DamagedPartTag>
                ))}
              </DamagedPartsList>
              {claim.mechanicalIssueOtherDescription && (
                <p className="mt-3 text-gray-600">{claim.mechanicalIssueOtherDescription}</p>
              )}
            </Section>
          )}

        {/* Photos */}
        <Section>
          <SectionTitle>
            <SectionIcon>
              <FontAwesomeIcon icon={faCamera} />
            </SectionIcon>
            Photos
          </SectionTitle>

          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Before Photos</h3>
            {claim.beforePhotos && claim.beforePhotos.length > 0 ? (
              <PhotosGrid>
                {claim.beforePhotos.map((photo: string, index: number) => {
                  const src = toPublicS3ObjectUrl(photo);
                  return (
                    <PhotoThumbnail
                      key={index}
                      src={src}
                      alt={`Before photo ${index + 1}`}
                      onClick={() => window.open(src, '_blank')}
                    />
                  );
                })}
              </PhotosGrid>
            ) : (
              <NoPhotos>No before photos provided</NoPhotos>
            )}
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">After Photos (Damage)</h3>
            {claim.afterPhotos && claim.afterPhotos.length > 0 ? (
              <PhotosGrid>
                {claim.afterPhotos.map((photo: string, index: number) => {
                  const src = toPublicS3ObjectUrl(photo);
                  return (
                    <PhotoThumbnail
                      key={index}
                      src={src}
                      alt={`After photo ${index + 1}`}
                      onClick={() => window.open(src, '_blank')}
                    />
                  );
                })}
              </PhotosGrid>
            ) : (
              <NoPhotos>No after photos provided</NoPhotos>
            )}
          </div>
        </Section>

        {/* Incident Details (Physical Damage) */}
        {claim.damageType === DamageType.Physical && (
          <Section>
            <SectionTitle>
              <SectionIcon>
                <FontAwesomeIcon icon={faMapMarkerAlt} />
              </SectionIcon>
              Incident Details
            </SectionTitle>
            <Grid>
              {claim.incidentOccurredAt && (
                <InfoItem>
                  <InfoLabel>When did the incident occur?</InfoLabel>
                  <InfoValue>{formatDateTime(claim.incidentOccurredAt)}</InfoValue>
                </InfoItem>
              )}
              {claim.incidentLocation && (
                <InfoItem>
                  <InfoLabel>Where did the incident occur?</InfoLabel>
                  <InfoValue>
                    {[
                      claim.incidentLocation.streetAddress,
                      claim.incidentLocation.city,
                      claim.incidentLocation.postalCode,
                      claim.incidentLocation.country,
                    ]
                      .filter(Boolean)
                      .join(', ') || 'Not specified'}
                  </InfoValue>
                </InfoItem>
              )}
            </Grid>
          </Section>
        )}

        {/* Assessment Questions (Physical Damage) */}
        {claim.damageType === DamageType.Physical && (
          <Section>
            <SectionTitle>
              <SectionIcon>
                <FontAwesomeIcon icon={faFileAlt} />
              </SectionIcon>
              Assessment
            </SectionTitle>
            <Grid>
              {claim.isCarDriveable && (
                <InfoItem>
                  <InfoLabel>Is the car driveable?</InfoLabel>
                  <InfoValue>{getYesNoLabel(claim.isCarDriveable)}</InfoValue>
                </InfoItem>
              )}
              {claim.policeReportFiled && (
                <InfoItem>
                  <InfoLabel>Was a police report filed?</InfoLabel>
                  <InfoValue>{getYesNoLabel(claim.policeReportFiled)}</InfoValue>
                </InfoItem>
              )}
              {claim.whoWasDriving && (
                <InfoItem>
                  <InfoLabel>Who was driving?</InfoLabel>
                  <InfoValue>{getDriverLabel(claim.whoWasDriving)}</InfoValue>
                </InfoItem>
              )}
              {claim.anyoneInjured && (
                <InfoItem>
                  <InfoLabel>Was anyone injured?</InfoLabel>
                  <InfoValue>{getYesNoLabel(claim.anyoneInjured)}</InfoValue>
                </InfoItem>
              )}
              {claim.anotherPersonInvolved && (
                <InfoItem>
                  <InfoLabel>Was another person involved?</InfoLabel>
                  <InfoValue>{getYesNoLabel(claim.anotherPersonInvolved)}</InfoValue>
                </InfoItem>
              )}
              {claim.didAirbagsDeploy && (
                <InfoItem>
                  <InfoLabel>Did airbags deploy?</InfoLabel>
                  <InfoValue>{getYesNoLabel(claim.didAirbagsDeploy)}</InfoValue>
                </InfoItem>
              )}
              {claim.didVehicleLoseFluids && (
                <InfoItem>
                  <InfoLabel>Did vehicle lose fluids?</InfoLabel>
                  <InfoValue>{getYesNoLabel(claim.didVehicleLoseFluids)}</InfoValue>
                </InfoItem>
              )}
              {claim.waterInsideCar && (
                <InfoItem>
                  <InfoLabel>Did water get inside the car?</InfoLabel>
                  <InfoValue>{getYesNoLabel(claim.waterInsideCar)}</InfoValue>
                </InfoItem>
              )}
            </Grid>
          </Section>
        )}

        {/* Damage Description */}
        {claim.damageDescription && (
          <Section>
            <SectionTitle>
              <SectionIcon>
                <FontAwesomeIcon icon={faFileAlt} />
              </SectionIcon>
              Damage Description
            </SectionTitle>
            <p className="text-gray-700 whitespace-pre-wrap">{claim.damageDescription}</p>
          </Section>
        )}

        {/* Repair Shop Info */}
        {claim.repairShopInfo &&
          (claim.repairShopInfo.shopName ||
            claim.repairShopInfo.address ||
            claim.repairShopInfo.contactName ||
            claim.repairShopInfo.phoneNumber) && (
            <Section>
              <SectionTitle>
                <SectionIcon>
                  <FontAwesomeIcon icon={faWrench} />
                </SectionIcon>
                Repair Shop Information
              </SectionTitle>
              <RepairShopCard>
                <Grid>
                  {claim.repairShopInfo.shopName && (
                    <InfoItem>
                      <InfoLabel>Shop Name</InfoLabel>
                      <InfoValue>{claim.repairShopInfo.shopName}</InfoValue>
                    </InfoItem>
                  )}
                  {claim.repairShopInfo.address && (
                    <InfoItem>
                      <InfoLabel>Address</InfoLabel>
                      <InfoValue>{claim.repairShopInfo.address}</InfoValue>
                    </InfoItem>
                  )}
                  {claim.repairShopInfo.contactName && (
                    <InfoItem>
                      <InfoLabel>Contact Person</InfoLabel>
                      <InfoValue>{claim.repairShopInfo.contactName}</InfoValue>
                    </InfoItem>
                  )}
                  {claim.repairShopInfo.phoneNumber && (
                    <InfoItem>
                      <InfoLabel>Phone Number</InfoLabel>
                      <InfoValue>{claim.repairShopInfo.phoneNumber}</InfoValue>
                    </InfoItem>
                  )}
                </Grid>
              </RepairShopCard>
            </Section>
          )}

        {/* Additional Info */}
        {claim.additionalInfo && (
          <Section>
            <SectionTitle>
              <SectionIcon>
                <FontAwesomeIcon icon={faFileAlt} />
              </SectionIcon>
              Additional Information
            </SectionTitle>
            <p className="text-gray-700 whitespace-pre-wrap">{claim.additionalInfo}</p>
          </Section>
        )}

        {/* Counterparty Response Section */}
        <ResponseSection>
          <SectionTitle>
            <SectionIcon>
              <FontAwesomeIcon icon={faReply} />
            </SectionIcon>
            Counterparty Response
            <ResponseStatusBadge reviewed={claim.counterpartyReviewed}>
              {claim.counterpartyReviewed ? 'Responded' : 'Awaiting Response'}
            </ResponseStatusBadge>
          </SectionTitle>

          {/* Response Deadline */}
          {claim.responseDeadline && timeRemaining && !claim.counterpartyReviewed && (
            <ResponseDeadlineBar isExpired={timeRemaining.isExpired}>
              <FontAwesomeIcon icon={timeRemaining.isExpired ? faExclamationCircle : faHourglass} />
              <span>{timeRemaining.text}</span>
            </ResponseDeadlineBar>
          )}

          {/* Existing Response */}
          {claim.response && claim.counterpartyReviewed && (
            <ExistingResponse>
              <ResponseHeader>
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />
                <ResponderName>
                  {claim.responder
                    ? claim.responder.displayName ||
                      `${claim.responder.firstName} ${claim.responder.lastName}`
                    : 'Counterparty'}
                </ResponderName>
                <ResponseDate>responded on {formatDate(claim.response.createdAt)}</ResponseDate>
              </ResponseHeader>
              <ResponseContent>{claim.response.responseContent}</ResponseContent>
              {claim.response.responsePhotos && claim.response.responsePhotos.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Attached photos</p>
                  <PhotosGrid>
                    {claim.response.responsePhotos.map((url: string, idx: number) => {
                      const src = toPublicS3ObjectUrl(url);
                      return (
                        <PhotoThumbnail
                          key={`resp-${idx}`}
                          src={src}
                          alt={`Response photo ${idx + 1}`}
                          onClick={() => window.open(src, '_blank')}
                        />
                      );
                    })}
                  </PhotosGrid>
                </div>
              )}
            </ExistingResponse>
          )}

          {/* Response Form */}
          {canRespondData?.canRespondToClaim.canRespond && !claim.counterpartyReviewed && (
            <ResponseForm onSubmit={handleSubmitResponse}>
              <label className="block text-sm font-medium text-gray-700 mb-2">Your Response</label>
              <ResponseTextarea
                value={responseText}
                onChange={e => setResponseText(e.target.value)}
                placeholder="Please provide your response to this damage claim..."
              />
              <label className="block text-sm font-medium text-gray-700 mb-2 mt-4">
                Evidence (optional)
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Add up to {MAX_RESPONSE_PHOTOS} photos — upload new images or select from this
                booking&apos;s trip photos.
              </p>
              <ResponsePhotoActions>
                <SecondaryButton
                  type="button"
                  onClick={() => responseFileInputRef.current?.click()}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Upload
                </SecondaryButton>
                <SecondaryButton
                  type="button"
                  onClick={() => setTripPickerOpen(true)}
                  disabled={tripPickerItems.length === 0}
                >
                  <FontAwesomeIcon icon={faImages} />
                  Trip photos
                </SecondaryButton>
                <HiddenMultiFileInput
                  ref={responseFileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={onResponseFilesSelected}
                />
              </ResponsePhotoActions>
              {totalResponsePhotoCount > 0 && (
                <ResponsePhotoPreviewGrid>
                  {responseTripUrls.map((url, idx) => (
                    <ResponsePreviewCard key={`t-${url}-${idx}`}>
                      <ResponseThumbImg src={url} alt="" />
                      <RemovePreviewBtn
                        type="button"
                        aria-label="Remove"
                        onClick={() => removeResponseTripUrl(url)}
                      >
                        <FontAwesomeIcon icon={faTrash} size="xs" />
                      </RemovePreviewBtn>
                    </ResponsePreviewCard>
                  ))}
                  {responseFilePreviewUrls.map((url, idx) => (
                    <ResponsePreviewCard key={`f-${idx}`}>
                      <ResponseThumbImg src={url} alt="" />
                      <RemovePreviewBtn
                        type="button"
                        aria-label="Remove"
                        onClick={() => removeResponseFile(idx)}
                      >
                        <FontAwesomeIcon icon={faTrash} size="xs" />
                      </RemovePreviewBtn>
                    </ResponsePreviewCard>
                  ))}
                </ResponsePhotoPreviewGrid>
              )}
              <SubmitButton type="submit" disabled={respondLoading || !responseText.trim()}>
                {respondLoading ? (
                  <ScaleLoader color="#ffffff" height={15} />
                ) : (
                  <>
                    <FontAwesomeIcon icon={faReply} />
                    Submit Response
                  </>
                )}
              </SubmitButton>
            </ResponseForm>
          )}

          {tripPickerOpen && (
            <TripPickerBackdrop onClick={() => setTripPickerOpen(false)}>
              <TripPickerPanel onClick={e => e.stopPropagation()}>
                <TripPickerHeader>
                  <span className="font-semibold text-gray-900">Select trip photos</span>
                  <button
                    type="button"
                    className="text-sm text-pink-600 font-medium"
                    onClick={() => setTripPickerOpen(false)}
                  >
                    Done
                  </button>
                </TripPickerHeader>
                <TripPickerScroll>
                  <TripThumbGrid>
                    {tripPickerItems.map(item => (
                      <TripThumb
                        key={item.key}
                        type="button"
                        $selected={responseTripUrls.includes(item.resolved)}
                        onClick={() => toggleTripPhoto(item.resolved)}
                        title={item.label}
                      >
                        <TripThumbImg src={item.resolved} alt="" loading="lazy" />
                        <span className="absolute bottom-1 left-1 text-[10px] bg-black/50 text-white px-1 rounded">
                          {item.label}
                        </span>
                      </TripThumb>
                    ))}
                  </TripThumbGrid>
                  {tripPickerItems.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-6">
                      No trip photos on this booking yet.
                    </p>
                  )}
                </TripPickerScroll>
              </TripPickerPanel>
            </TripPickerBackdrop>
          )}

          {/* Cannot Respond Message */}
          {!canRespondData?.canRespondToClaim.canRespond && !claim.counterpartyReviewed && (
            <NoResponseNeeded>
              <FontAwesomeIcon icon={faExclamationCircle} />
              <span>
                {canRespondData?.canRespondToClaim.reason || 'You cannot respond to this claim'}
              </span>
            </NoResponseNeeded>
          )}
        </ResponseSection>

        {/* Admin Notes (if any) */}
        {claim.adminNotes && (
          <Section>
            <SectionTitle>
              <SectionIcon>
                <FontAwesomeIcon icon={faUser} />
              </SectionIcon>
              Admin Notes
            </SectionTitle>
            <p className="text-gray-700 whitespace-pre-wrap">{claim.adminNotes}</p>
          </Section>
        )}

        {/* Timeline */}
        <Section>
          <SectionTitle>
            <SectionIcon>
              <FontAwesomeIcon icon={faClock} />
            </SectionIcon>
            Timeline
          </SectionTitle>
          <div className="space-y-3">
            <InfoItem>
              <InfoLabel>Created</InfoLabel>
              <InfoValue>{formatDate(claim.createdAt)}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Last Updated</InfoLabel>
              <InfoValue>{formatDate(claim.updatedAt)}</InfoValue>
            </InfoItem>
            {claim.reviewedAt && (
              <InfoItem>
                <InfoLabel>Reviewed</InfoLabel>
                <InfoValue>{formatDate(claim.reviewedAt)}</InfoValue>
              </InfoItem>
            )}
          </div>
        </Section>
      </ContentContainer>
    </PageContainer>
  );
};

export default DamageClaimDetailPage;
